import React, { useEffect, useCallback } from 'react'

import { convertMegabytesToBytes } from 'utils'

import { useSnackbar } from 'contexts'
import { useUncontrolled } from 'hooks'
import { useDropzone, type FileWithPath } from 'react-dropzone'

import { Box, Stack, Button, IconButton, Avatar } from '@mui/material'

import { XIcon } from 'lucide-react'

interface UploadLogoProps {
  file?: File[]
  onChange?: (files: File[]) => void
}

type File = {
  file?: FileWithPath | null
  preview_url: string
  isUploaded: boolean
}

export const UploadLogo = (props: UploadLogoProps) => {
  const { file, onChange } = props

  const [files, setFiles] = useUncontrolled<File[]>({
    value: file,
    defaultValue: [],
    finalValue: [],
    onChange: value => onChange?.(value),
  })

  const { open: notification } = useSnackbar()

  const onDrop = useCallback((acceptedFiles: FileWithPath[], rejectedFiles: any[]) => {
    const acceptedFilesWithPreview = acceptedFiles.map(file => ({
      file: file,
      preview_url: URL.createObjectURL(file),
      isUploaded: false,
    }))

    if (acceptedFilesWithPreview.length > 0) {
      setFiles(acceptedFilesWithPreview)
    }

    if (rejectedFiles.length > 0) {
      const { errors } = rejectedFiles[0]

      errors.forEach((error: any) => {
        if (error.code === 'file-too-large') {
          notification({
            type: 'error',
            title: 'File is too large. Max image size 2mb.',
          })
        }

        if (error.code === 'file-invalid-type') {
          notification({
            type: 'error',
            title: 'You can upload "png", "jpg", "jpeg" file formats only',
          })
        }
      })
    }
  }, [notification, setFiles])

  const { getRootProps, getInputProps, open: openFileDialog } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    noClick: true,
    noKeyboard: true,
    maxSize: convertMegabytesToBytes(2),
    maxFiles: 1,
    onDrop,
  })

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => files.forEach(file => URL.revokeObjectURL(file.preview_url))
  }, [])

  const handleDelete = () => {
    setFiles([])
  }

  const isHasFiles = files.length > 0

  return (
    <Box {...getRootProps()}>
      <input {...getInputProps()} />
      {isHasFiles ? (
        <Stack direction="row" spacing="24px" alignItems="center">
          <Avatar src={files[0].preview_url} sx={{ width: 64, height: 64 }} />

          <Stack direction="row" spacing="16px" alignItems="center">
            <Button 
              variant="outlined" 
              onClick={(e) => {
                e.stopPropagation()
                openFileDialog()
              }} 
              sx={{ width: 115 }}
            >
              Edit
            </Button>
            <IconButton 
              onClick={(e) => {
                e.stopPropagation()
                handleDelete()
              }}
            >
              <XIcon strokeWidth={1} />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Button 
          variant="outlined" 
          onClick={(e) => {
            e.stopPropagation()
            openFileDialog()
          }} 
          sx={{ width: 115 }}
        >
          Upload
        </Button>
      )}
    </Box>
  )
}
